import * as Yup from 'yup';

import type { CountryCode } from 'types/countries';
import { fieldOfStudyMarketVisible, isEducationRegionCountry } from 'utils/countries';

import { validateField, ValidationType } from './formValidation';

/** Required when that market's field-of-study input is shown (not High school). */
const fieldOfStudyIdSchema = (enabled: readonly string[] | null | undefined, code: CountryCode) =>
    Yup.mixed()
        .nullable()
        .when('type', {
            is: (type?: string | null) => fieldOfStudyMarketVisible(enabled, type, code),
            then: (schema) => schema.required('This is a required field'),
            otherwise: (schema) => schema.nullable(),
        });

/** BOOLEAN fields may arrive as real booleans or as "true" / "false" strings. */
const isYes = (value: unknown) => value === true || value === 'true';

/** End date is required only when the program is completed and not ongoing. */
const endDateSchema = Yup.mixed().when(['current', 'completed'], {
    is: (current: unknown, completed: unknown) => isYes(completed) && !isYes(current),
    then: (schema) => validateField(ValidationType.STRING),
    otherwise: (schema) => schema.nullable().notRequired(),
});

/**
 * Education entry aligned with AssessmentV2/Education.tsx validate():
 * - region required only for AU/CA/UK (incl. FR labels)
 * - field_of_study_*_id required when that enabled market's field is visible
 * - language, institution_name, and zip are optional even when shown
 * - end is required only when completed and not ongoing
 */
export const createEducationItemSchema = (enabled: readonly string[] | null | undefined) =>
    Yup.object({
        type: validateField(ValidationType.STRING),
        country: validateField(ValidationType.STRING),
        language: validateField(ValidationType.STRING, false),
        institution_name: validateField(ValidationType.STRING, false),
        zip: validateField(ValidationType.STRING, false),
        completed: validateField(ValidationType.BOOLEAN),
        current: validateField(ValidationType.BOOLEAN),
        start: validateField(ValidationType.STRING),
        end: endDateSchema,
        region: Yup.string()
            .nullable()
            .when('country', {
                is: isEducationRegionCountry,
                then: (schema) => schema.required('Region is required'),
                otherwise: (schema) => schema.nullable(),
            }),
        field_of_study_au_id: fieldOfStudyIdSchema(enabled, 'AU'),
        field_of_study_ca_id: fieldOfStudyIdSchema(enabled, 'CA'),
        field_of_study_uk_id: fieldOfStudyIdSchema(enabled, 'UK'),
    });

export const spouseEducationItemSchema = Yup.object({
    completed: validateField(ValidationType.BOOLEAN),
    country: validateField(ValidationType.STRING),
    current: validateField(ValidationType.BOOLEAN),
    duration: validateField(ValidationType.NUMBER),
    type: validateField(ValidationType.STRING),
    start: validateField(ValidationType.STRING),
    end: endDateSchema,
    region: Yup.string()
        .nullable()
        .when('country', {
            is: isEducationRegionCountry,
            then: (schema) => schema.required('Region is required'),
            otherwise: (schema) => schema.nullable(),
        }),
});

export const createEducationValidationSchema = (enabled: readonly string[] | null | undefined) =>
    Yup.object({
        education: Yup.array().of(createEducationItemSchema(enabled)).min(1),
        spouse_education: Yup.array().of(spouseEducationItemSchema),
    });
